import { useActiveOfficialTellerSettings, useCounselAnswers } from '@raysee/hooks';
import {
  Counsel,
  CounselState,
  Coupon,
  CouponCode,
  extractCouponCode,
  LineAccount,
  Message,
  MessageType,
  Room,
  Teller,
  User,
} from '@raysee/schema';
import { extractUrl } from '@raysee/utility';
import { FC, useEffect, useRef } from 'react';
import { use100vh } from 'react-div-100vh';
import { CallAgainBubble } from './bubbles/CallAgainBubble';
import { CallBubble } from './bubbles/CallBubble';
import { CanceledBubble } from './bubbles/CanceledBubble';
import { CarouselBubble } from './bubbles/CarouselBubble';
import { ChoiceBubble } from './bubbles/ChoiceBubble';
import { ConfirmationBubble } from './bubbles/ConfirmationBubble';
import { ConfirmationResultBubble } from './bubbles/ConfirmationResultBubble';
import { CouponBubble } from './bubbles/CouponBubble';
import { CouponBubble2 } from './bubbles/CouponBubble2';
import { FinishedBubble } from './bubbles/FinishedBubble';
import { ImageBubble } from './bubbles/ImageBubble';
import { InputDateBubble } from './bubbles/InputDateBubble';
import { InputInvitationCodeBubble } from './bubbles/InputInvitationCodeBubble';
import { InputNameBubble } from './bubbles/InputNameBubble';
import { InputTextBubble } from './bubbles/InputTextBubble';
import { LoadingBubble } from './bubbles/LoadingBubble';
import { OptionsBubble } from './bubbles/OptionsBubble';
import { PhoneBubble } from './bubbles/PhoneBubble';
import { PlanCarouselBubble } from './bubbles/PlanCarouselBubble';
import { PlanTitleBubble } from './bubbles/PlanTitleBubble';
import { RequestedBubble } from './bubbles/RequestedBubble';
import { SalesBubble } from './bubbles/SalesBubble';
import { StartedBubble } from './bubbles/StartedBubble';
import { SuggestCouponBubble } from './bubbles/SuggestCouponBubble';
import { TellersBubble } from './bubbles/TellersBubble';
import { TextBubble } from './bubbles/TextBubble';
import { TimerBubble } from './bubbles/TimerBubble';
import { InducePopProvider, InducePopupConfig } from './InducePopProvider';
import { ToolBar } from './ToolBar';
import type { UploadError } from './UploadErrorBar';
import type { UploadState } from './UploadStatusBar';

type Role = 'teller' | 'user';

interface Props {
  room: Pick<Room, 'id' | 'status' | 'userViewedTime' | 'createTime'>;
  messages: Message[];
  teller?: Pick<Teller, 'name' | 'imageUrl'>;
  user: Pick<User, 'id' | 'name' | 'imageUrl'>;
  role: Role;
  counsel?: Counsel;
  inducePopupConfig?: InducePopupConfig;
  coupons?: Coupon[];
  counselCoupon?: CouponCode;
  hideToolBar?: boolean;
  isDataLoading?: boolean;
  isLoading?: boolean;
  disableCancel?: boolean;
  chatText?: string;
  isMobile?: boolean;
  scroller?: (originScroll: () => void) => void;
  onCall?: () => void;
  onClick?: (url: string, message: Message) => void;
  onSend: (text: string, isNotification?: boolean) => void;
  onUpload?: (file: File) => void;
  /** 画像アップロード中の状態(進捗・停止)。null/未指定なら表示しない */
  uploadState?: UploadState | null;
  onCancelUpload?: () => void;
  /** 画像アップロードの失敗(原因の案内)。null/未指定なら表示しない */
  uploadError?: UploadError | null;
  onRetryUpload?: () => void;
  onDismissUploadError?: () => void;
  onClickOption?: () => void;
  onClickFinish?: () => void;
  onClickCancel?: () => void;
  onChangeText?: (text: string) => void;
  lineAccount?: LineAccount;
  spStaffRooms?: boolean;
  isPhone?: boolean;
}

const DUMMY_SENDER = {
  name: 'メエシィ',
  imageUrl:
    'https://firebasestorage.googleapis.com/v0/b/luminous-prod.appspot.com/o/counsels%2Fconcierge.png?alt=media&token=277ea268-8fa6-46c0-a95f-2e420d142782',
};

export const Chat: FC<Props> = ({
  room,
  messages,
  teller,
  user,
  role,
  counsel,
  coupons,
  counselCoupon,
  hideToolBar,
  isDataLoading,
  isLoading,
  disableCancel,
  chatText,
  isMobile,
  scroller,
  onClickOption,
  onClickFinish,
  onClickCancel,
  onCall,
  onClick,
  onSend,
  onUpload,
  uploadState,
  onCancelUpload,
  uploadError,
  onRetryUpload,
  onDismissUploadError,
  onChangeText,
  lineAccount,
  spStaffRooms,
  isPhone,
  inducePopupConfig,
}) => {
  const endView = useRef<HTMLDivElement>(null);
  const answerMessageView = useRef<HTMLDivElement>(null);
  const height = use100vh();
  const [answers] = useCounselAnswers();
  const [officialTellerSettings] = useActiveOfficialTellerSettings();
  const officialTellerCardImageUrls =
    officialTellerSettings
      ?.filter(
        (setting) =>
          !setting.disabled && !!setting.counselCard && !setting.counselCard?.disabled,
      )
      .map((setting) => setting.counselCard.imageUrl) ?? [];

  useEffect(() => {
    if (messages.length <= 1) return;

    if (counsel) {
      if (counsel.state === CounselState.Finish) {
        scrollToAnswerMessage();
      } else {
        scrollToBottom();
      }
    } else if (!!scroller) {
      scroller(scrollToBottom);
    } else {
      scrollToBottom();
    }
  }, [messages, isLoading, scroller]);

  const scrollToBottom = () => {
    endView.current?.scrollIntoView({ behavior: 'auto' });
  };

  const scrollToAnswerMessage = () => {
    answerMessageView.current?.scrollIntoView({ behavior: 'auto', block: 'center' });
  };

  const onEnterSend = (text: string, isNotification?: boolean) => {
    switch (text) {
      case '鑑定終了':
        const res = window.confirm('鑑定終了していいですか？');
        if (res) {
          onSend('鑑定終了', true);
        }
        break;
      case '通話開始':
        onCall && onCall();
        break;
      default:
        isNotification ? onSend(text, true) : onSend(text);
    }
  };

  const isAlreadyCalled = messages.find((m) => m.text === '📞 通話が開始しました');

  const sender =
    role === 'teller'
      ? {
          name: user.name + 'さん',
          imageUrl: user.imageUrl ?? '',
        }
      : {
          name: teller?.name ?? '' + '先生',
          imageUrl: teller?.imageUrl ?? '',
        };

  return (
    <InducePopProvider popupConfig={inducePopupConfig}>
      <div className="flex-grow">
        <div
          id="chat"
          className="overflow-y-scroll bg-[url('/chat-background.jpeg')] bg-cover bg-center bg-no-repeat py-2"
          style={
            isMobile
              ? { height: hideToolBar ? '100vh' : 'calc(100vh - 71px)' }
              : { height: hideToolBar ? height - 57 : height - 57 - 71 }
          }
        >
          {messages
            .sort((a, b) => (a.createTime > b.createTime ? 1 : -1))
            .map((message, i) => {
              const props = {
                room,
                sender: message.senderId
                  ? role !== message.senderType
                    ? sender
                    : null
                  : message.type === MessageType.PlanTitle && role === 'user'
                    ? sender
                    : DUMMY_SENDER,
                role,
                onSend: onEnterSend,
                spStaffRooms,
                isPhone,
              };
              const isLast = i === messages.length - 1;
              const url = extractUrl(message.text);
              const code = extractCouponCode(url);

              const type =
                message.type !== MessageType.Text
                  ? message.type
                  : code &&
                      (answers?.some((answer) => answer.couponCode === code) || code === '6VjKwhQx')
                    ? MessageType.Coupon2
                    : message.type;

              return {
                [MessageType.Text]:
                  message.id === 'answer' ? (
                    <div key={message.id} ref={answerMessageView}>
                      <TextBubble
                        {...props}
                        message={message}
                        onClick={onClick}
                        officialTellerCardImageUrls={officialTellerCardImageUrls}
                      />
                    </div>
                  ) : (
                    <TextBubble
                      {...props}
                      key={message.id}
                      message={message}
                      onClick={onClick}
                      officialTellerCardImageUrls={officialTellerCardImageUrls}
                    />
                  ),
                [MessageType.PlanTitle]: (
                  <PlanTitleBubble
                    {...props}
                    key={message.id}
                    message={message}
                    onClick={onClick}
                  />
                ),
                [MessageType.WaitingText]: (
                  <TextBubble
                    {...props}
                    key={message.id}
                    message={message}
                    officialTellerCardImageUrls={officialTellerCardImageUrls}
                  />
                ),
                [MessageType.RetargetingText]: (
                  <TextBubble
                    {...props}
                    key={message.id}
                    message={message}
                    officialTellerCardImageUrls={officialTellerCardImageUrls}
                  />
                ),
                [MessageType.Image]: (
                  <ImageBubble
                    {...props}
                    key={message.id}
                    message={message}
                    fullSize={message.fullSize}
                  />
                ),
                [MessageType.Requested]: (
                  <RequestedBubble {...props} key={message.id} message={message} />
                ),
                [MessageType.Started]: (
                  <StartedBubble {...props} key={message.id} message={message} />
                ),
                [MessageType.Finished]: (
                  <FinishedBubble {...props} key={message.id} message={message} />
                ),
                [MessageType.Timer]: <TimerBubble {...props} key={message.id} message={message} />,
                [MessageType.Canceled]: (
                  <CanceledBubble {...props} key={message.id} message={message} />
                ),
                [MessageType.Phone]: <PhoneBubble {...props} key={message.id} message={message} />,
                [MessageType.Call]: (
                  <CallBubble
                    {...props}
                    key={message.id}
                    message={message}
                    isAlreadyCalled={isAlreadyCalled}
                  />
                ),
                [MessageType.CallAgain]: (
                  <CallAgainBubble {...props} key={message.id} message={message} />
                ),
                [MessageType.Choice]: (
                  <ChoiceBubble {...props} key={message.id} message={message} isWaiting={isLast} />
                ),
                [MessageType.ChoiceResult]: (
                  <OptionsBubble {...props} key={message.id} message={message} />
                ),
                [MessageType.ChoiceTeller]: (
                  <TellersBubble
                    {...props}
                    message={message}
                    selectedTellerId={counsel?.data?.tellerId}
                  />
                ),
                [MessageType.Confirmation]: (
                  <ConfirmationBubble
                    {...props}
                    key={message.id}
                    message={message}
                    isWaiting={isLast}
                  />
                ),
                [MessageType.ConfirmationResult]: (
                  <ConfirmationResultBubble {...props} key={message.id} message={message} />
                ),
                [MessageType.Coupon]: (
                  <CouponBubble {...props} key={message.id} message={message} />
                ),
                [MessageType.Coupon2]: (
                  <CouponBubble2
                    {...props}
                    message={message}
                    url={url}
                    onClick={onClick}
                    couponMessage={messages.find((m) => m.text.includes('code='))}
                  />
                ),
                [MessageType.InputDate]: (
                  <InputDateBubble
                    {...props}
                    key={message.id}
                    message={message}
                    isWaiting={isLast}
                  />
                ),
                [MessageType.InvitationCode]: (
                  <InputInvitationCodeBubble
                    {...props}
                    key={message.id}
                    message={message}
                    isWaiting={isLast}
                  />
                ),
                [MessageType.InputText]: (
                  <InputTextBubble
                    {...props}
                    key={message.id}
                    message={message}
                    isWaiting={isLast}
                  />
                ),
                [MessageType.Carousel]: (
                  <CarouselBubble
                    {...props}
                    key={message.id}
                    message={message}
                    isWaiting={isLast}
                    selectedChoice={counsel?.data?.problem}
                    disableContext={counsel && counsel.type === 'new_year_2024'}
                    scrollToBottom={scrollToBottom}
                  />
                ),
                [MessageType.PlanCarousel]: (
                  <PlanCarouselBubble
                    {...props}
                    key={message.id}
                    message={message}
                    isWaiting={isLast}
                    onClick={onClick}
                  />
                ),
                [MessageType.MyName]: (
                  <InputNameBubble
                    {...props}
                    key={message.id}
                    message={message}
                    isWaiting={isLast}
                    submitted={!!counsel?.data?.myName}
                  />
                ),
                [MessageType.MyGender]: (
                  <OptionsBubble {...props} key={message.id} message={message} />
                ),
                [MessageType.MyBirthday]: (
                  <TextBubble
                    {...props}
                    key={message.id}
                    message={message}
                    officialTellerCardImageUrls={officialTellerCardImageUrls}
                  />
                ),
                [MessageType.YourName]: (
                  <InputNameBubble
                    {...props}
                    key={message.id}
                    message={message}
                    isWaiting={isLast}
                    submitted={!!counsel?.data?.yourName}
                  />
                ),
                [MessageType.YourBirthday]: (
                  <TextBubble
                    {...props}
                    key={message.id}
                    message={message}
                    officialTellerCardImageUrls={officialTellerCardImageUrls}
                  />
                ),
                [MessageType.ProblemDetail]: (
                  <TextBubble
                    {...props}
                    key={message.id}
                    message={message}
                    officialTellerCardImageUrls={officialTellerCardImageUrls}
                  />
                ),
                [MessageType.Sales]: (
                  <SalesBubble
                    {...props}
                    key={message.id}
                    message={message}
                    coupons={coupons ?? []}
                    onClick={onClick}
                  />
                ),
              }[type];
            })}
          {isDataLoading && <LoadingBubble sender={DUMMY_SENDER} />}
          {isLoading && (
            <LoadingBubble sender={counsel.type === 'manual' ? DUMMY_SENDER : sender} />
          )}
          {counsel && counsel.state === CounselState.Finish && !!counsel.couponUrl && (
            <SuggestCouponBubble
              couponCode={counselCoupon}
              userId={user.id}
              couponMessage={messages.find((m) => m.text.includes('code='))}
              onClick={onClick}
            />
          )}
          <div ref={endView} />
        </div>
        {!hideToolBar && (
          <div className="sticky bottom-0">
            <ToolBar
              enableFinish={false}
              defaultText={
                counsel?.state === CounselState.InputProblemDetail
                  ? counsel.data.problemDetail ?? ''
                  : chatText ?? ''
              }
              disableCancel={disableCancel}
              onSend={onEnterSend}
              onUpload={onUpload}
              uploadState={uploadState}
              onCancelUpload={onCancelUpload}
              uploadError={uploadError}
              onRetryUpload={onRetryUpload}
              onDismissUploadError={onDismissUploadError}
              onClickOption={onClickOption}
              onClickFinish={onClickFinish}
              onClickCancel={onClickCancel}
              onChangeText={onChangeText}
              counselState={counsel?.state}
              lineAccount={lineAccount}
            />
          </div>
        )}
      </div>
    </InducePopProvider>
  );
};
